import { useEffect, useRef, useState } from 'react'
import { api } from './api'
import { Abrufausfall, useAbruf } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Auswahlfeld } from './Auswahlfeld'
import { Dialogfenster } from './Dialogfenster'
import { beschriftung, katalogbezeichnung, mitNamen, t, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import type { Anlagevorschau } from './typen'

/**
 * Eine Struktur dort anlegen, wo ihr Fehlen auffällt — am Bericht
 * (7.4 Abschnitt 14, R494–R501).
 *
 * **Die Maske zerfällt in zwei Hälften, und die Trennlinie ist scharf:** Oben
 * wird eingetragen, was ohne das Objekt nicht existiert — Nummer
 * beziehungsweise Code, Bezeichnung, ab welcher Periode es gilt (R318). Unten
 * steht, **was es von seiner Stelle erbt**, mit Wert und Herkunft, und ist
 * nicht änderbar (R496).
 *
 * **Warum nicht änderbar:** Ein Anlageblatt, das jedes Feld überschreiben
 * lässt, ist ein zweites Eigenschaftsblatt — ohne Herkunftsanzeige, ohne die
 * Rückgabe an die Gruppe (R242) und ohne die Warnung vor rechnenden Feldern
 * (R243). Überschrieben wird danach im Blatt, dort, wo es auch später geändert
 * wird (R317).
 *
 * **Angelegt heißt gepflegt, nicht gerechnet** (R498). Das Objekt steht sofort
 * in `core` und ist sofort benutzbar; die Berichtszeile kommt mit dem nächsten
 * Lauf, weil ein Bericht festgeschriebene Ergebniszeilen liest (R186). Die
 * Maske sagt das in denselben Worten, in denen es nach einer Handbuchung
 * dasteht.
 */

/** Woher die Maske gerufen wurde und was sie anlegen soll. */
export type Anlageziel = {
  position: string
  /** Was in der Überschrift steht — der Code samt Bezeichnung der Zeile. */
  positionsname: string
  /** Vorbelegt aus dem Bericht, in der Maske änderbar (R499). */
  periode: number
  /**
   * Was entstehen soll.
   *
   * **`kfrzeile` ist die dritte Art und verhält sich anders** (R507): Eine
   * Zeile der Kapitalflussrechnung steht im Zeilenkatalog des Standards und
   * nicht im Positionsplan — sie trägt weder Konten noch einen
   * Gültigkeitszeitraum. Die Maske bleibt dieselbe; sie zeigt dort zwei Felder
   * weniger.
   */
  art: 'konto' | 'position' | 'kfrzeile'
  /**
   * In der Kapitalflussrechnung: **unter einer Position oder unter dem Band
   * eines Abschnitts** (2.1 Abschnitt 3.6, R1706). Bis zum 2026-10-14 gab es
   * statt des Bandes die *Zeile danach* (R1124).
   */
  kfrArt?: 'unter' | 'abschnitt'
  /** Ob unter dem Band eine Summenzeile entsteht statt einer Position (R1705). */
  summenzeile?: boolean
}

/**
 * **Was entstanden ist** — für die Kontoauswahl, die das neue Konto gleich
 * ins Feld setzt (7.2 Abschnitt 47, R1495, R1497). Der Bericht braucht es
 * nicht; er lädt einfach neu.
 */
export type Angelegt =
  | { art: 'konto'; schluessel: string; bezeichnung: string }
  | { art: 'position'; schluessel: string; bezeichnung: string }
  | { art: 'kfrzeile' }

export function Strukturanlage({
  ziel,
  aufSchliessen,
  aufAngelegt,
}: {
  ziel: Anlageziel
  aufSchliessen: () => void
  /** Nach dem Anlegen — für die Bereiche, die daneben eine Struktur zeigen. */
  aufAngelegt?: (angelegt: Angelegt) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [abPeriode, setAbPeriode] = useState<number | null>(ziel.periode)
  const [gruppe, setGruppe] = useState('')
  // **Eine Summenzeile fragt, was sie zählt** (R1358). Eine Position fragt
  // nur Nummer und Bezeichnung (2.1 Abschnitt 3.6, R1708); ihre Referenzen
  // kommen mit *Neue Referenz …*. Bis zum 2026-10-14 stand hier die
  // Referenztabelle, und ohne Referenz entstand keine Zeile (R1357).
  const [zaehlweise, setZaehlweise] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fertig, setFertig] = useState<Fertigmeldung | null>(null)

  const anlegenknopf = useRef<HTMLButtonElement>(null)
  const erstesFeld = useRef<HTMLInputElement>(null)

  // Was zuletzt vorgeschlagen wurde — damit ein zweiter Vorschlag nur den
  // ersten ersetzt und nie das, was der Anwender getippt hat.
  const vorgeschlagen = useRef<string | null>(null)

  const istKonto = ziel.art === 'konto'
  const istKfr = ziel.art === 'kfrzeile'
  const kfrArt = ziel.kfrArt ?? 'unter'

  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()
  const darfAnlegen = istKfr
    ? darf('kfrZeileAnlegen')
    : istKonto
      ? darf('kontoAnlegen')
      : darf('unterpositionAnlegen')

  // **Die Vorschau wird zur Periode gefragt, die in der Maske steht** (R499):
  // Welche Konten unter der Position hängen und was sie vererben, ist eine
  // zeitbezogene Auskunft (R1). Wer die Periode ändert und dieselbe Vererbung
  // stehen sähe, bekäme eine Zusage über einen anderen Zeitpunkt.
  //
  // Solange die Eingabe keine Periode ergibt, bleibt die letzte Auskunft
  // stehen: Beim Tippen ist jede Zwischenstufe kurz unvollständig, und eine
  // Tafel, die dabei verschwindet, flackert.
  const letztePeriode = useRef(abPeriode)
  if (abPeriode !== null) {
    letztePeriode.current = abPeriode
  }
  const fragePeriode = letztePeriode.current

  // **Die Vorschau trägt die Maske — ein Hauptabruf** (7.2 Abschnitt 39,
  // R1211). Bis zum 2026-10-06 lief sie still im Hintergrund (R1007), und ein
  // Ausfall ließ die Erbtafel wortlos weg; seither steht dort „Ließ sich
  // nicht laden" (R1210).
  const vorschauabruf = useAbruf<Anlagevorschau>(
    fragePeriode === null
      ? null
      : () =>
          istKfr
            ? api.kfrAnlagevorschau(ziel.position, fragePeriode, kfrArt)
            : api.anlagevorschau(ziel.position, fragePeriode),
    [ziel.position, fragePeriode, istKfr, kfrArt],
  )
  const vorschau = vorschauabruf.wert

  /*
    **Das erste Feld wird vorgeschlagen, nicht vorgeschrieben** (R574, R575).
    Die Vorschau sagt, wie die Reihe an dieser Stelle weiterzählt; hier landet
    das im Feld, das der Anwender ohnehin ausfüllt.

    **Überschrieben wird nicht.** Wer getippt hat, behält sein Getipptes — auch
    wenn die Periode wechselt und ein neuer Vorschlag kommt. Ersetzt wird nur
    ein leeres Feld oder der vorige Vorschlag, den niemand angefasst hat.

    **Beide Arten bekommen einen, aus verschiedenen Reihen** (R575, seit dem
    2026-09-16): Eine Position zählt in der Gliederung weiter, ein Konto im
    Kontenrahmen. Bis dahin blieb die Nummer leer, mit der Begründung, sie
    zähle nicht in der Gliederung — das stimmt und war trotzdem kein Grund:
    `0210` `0220` `0230` unter derselben Position ist eine Reihe, und wer dort
    anlegt, setzt sie fort.
  */
  const codevorschlag = istKonto
    ? (vorschau?.nummernvorschlag ?? null)
    : (vorschau?.codevorschlag ?? null)

  useEffect(() => {
    if (codevorschlag === null || codevorschlag === vorgeschlagen.current) {
      return
    }

    const vorher = vorgeschlagen.current
    vorgeschlagen.current = codevorschlag

    /*
      **Bei Konto und Position belegt der Vorschlag die BEZEICHNUNG vor**
      (R678, seit 2026-09-21) — mit einem Leerzeichen dahinter, damit der
      Anwender gleich weiterschreiben kann: aus `0245 ` wird `0245 Sonstige
      Rückstellungen`. Die Nummer steht seither in der Bezeichnung und nicht
      mehr in einem eigenen Feld.

      **Die Zeile der Kapitalflussrechnung behält ihren Code** (R676): Er ist
      dort die Fundstelle im Standard und keine Beschriftung.
    */
    if (istKfr) {
      setCode((bisher) => (bisher === '' || bisher === vorher ? codevorschlag : bisher))
      return
    }

    setName((bisher) =>
      bisher === '' || bisher === `${vorher} ` ? `${codevorschlag} ` : bisher,
    )
  }, [codevorschlag, istKfr])

  // Strg+S legt an — dieselbe Bedienung wie in der Buchungsmaske. Eine
  // Funktion, die nur die Maus kennt, gibt es nicht (R143). Esc hört seit dem
  // 2026-10-06 das übliche Fenster (7.2 Abschnitt 41, R1260).
  useEffect(() => {
    erstesFeld.current?.focus()

    function beiTaste(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault()
        anlegenknopf.current?.click()
      }
    }

    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  }, [])

  const hindernis = vorschau
    ? istKonto
      ? vorschau.kontoHindernis
      : vorschau.positionHindernis
    : null

  // Eine Summenzeile nur, wo die Vorschau sie zulässt (2.8 Abschnitt 13.46, R1358).
  const summenzeile = istKfr && ziel.summenzeile === true && vorschau?.summenzeileMoeglich === true

  // Die Kontengruppe ist nur dann eine Frage, wenn die Geschwister sie nicht
  // beantworten (R497). Wo sie es tun, wird sie nicht gefragt und steht
  // danach im Eigenschaftsblatt (R1578).
  const gruppePflicht = istKonto && vorschau !== null && vorschau.gruppe === null

  /*
    **Die Ziffernprüfung ist entfallen** (R676, 2026-09-21). Sie stand für die
    Kontonummer und prüfte ein Feld, das es nicht mehr gibt: Die Nummer steht in
    der Bezeichnung und darf dort alles sein, was ein Haus als Nummer führt --
    'A. I. 2.' so gut wie '1400'. Die Zeile der Kapitalflussrechnung behält ihren
    Code, und der war nie eine Zahl.
  */

  const bereit =
    vorschau !== null &&
    hindernis === null &&
    // **Ohne Code trägt die Bezeichnung die Identität** (R677). Die Zeile der
    // Kapitalflussrechnung verlangt ihren Code und seit dem 2026-10-06 auch
    // ihre Bezeichnung (2.8 Abschnitt 13.45, R1354) — vorher stand „8b 8b" da.
    name.trim().length > 0 &&
    (!istKfr || code.trim().length > 0) &&
    // **Leer ist keine Wahl** (R1355): eine Summenzeile mit ihrer Zählweise
    // (R1358); eine Position braucht keine Referenz (R1708).
    (!summenzeile || zaehlweise.length > 0) &&
    (!istKfr || ziel.summenzeile !== true || summenzeile) &&
    abPeriode !== null &&
    (!gruppePflicht || gruppe.length > 0)

  async function anlegen() {
    if (!vorschau || !bereit || laeuft || abPeriode === null || !darfAnlegen) {
      return
    }

    setLaeuft(true)
    setMeldung(null)

    /*
      **Was gelang, sagt das Kästchen oben rechts** (7.2 Abschnitt 34, R1130,
      R1131) — ohne Nummer, Code und Position. Wo die neue Zeile hängt, zeigt
      der Baum hinter diesem Fenster.
    */
    try {
      if (istKfr) {
        await api.kfrZeileAnlegen(ziel.position, code.trim(), name.trim() || null, {
          art: kfrArt,
          referenzen: [],
          zaehlweise: summenzeile ? zaehlweise : null,
        })

        meldeErfolg(t('erfolg.kfrzeile.angelegt'))
        setFertig({ offenePlaene: 0 })
        aufAngelegt?.({ art: 'kfrzeile' })
      } else if (istKonto) {
        const neu = await api.kontoAnlegen(
          ziel.position,
          name.trim() || null,
          gruppe || null,
          abPeriode,
        )

        meldeErfolg(t('erfolg.konto.angelegt'))
        setFertig({ offenePlaene: neu.offenePlaene.length })
        aufAngelegt?.({ art: 'konto', schluessel: neu.konto, bezeichnung: neu.bezeichnung })
      } else {
        const zug = await api.unterpositionAnlegen(ziel.position, name.trim() || null, abPeriode)

        meldeErfolg(t('erfolg.position.angelegt'))
        setFertig({ offenePlaene: 0 })
        aufAngelegt?.({ art: 'position', schluessel: zug.gegenstand, bezeichnung: name.trim() })
      }
    } catch (fehler) {
      // Befund mit Handreichung (R145); eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  /*
    **Das übliche Fenster** (7.2 Abschnitt 41, R1260): verschieben am Kopf,
    groß und klein im Kopf, die Ecke zieht, Esc und Kreuz schließen. Die
    Knopfzeile steht im Fuß und bleibt beim Rollen stehen (R915).
  */
  return (
    <Dialogfenster
      titel={t(titelschluessel(ziel.art, ziel.summenzeile), { position: ziel.positionsname })}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      kopfzusatz={
        /*
          **Die Klammer steht unveränderlich da** (R499): der Positionsplan, in
          dem gearbeitet wird. Er ist der des Laufs; eine Auswahl daraus zu
          machen hieße, eine Wahl anzubieten, die der Bericht schon getroffen
          hat.
        */
        vorschau
          ? [
              beschriftung(vorschau.plannamen) || vorschau.plancode,
              // Unter dem Band steht das Band im Titel; die Position, über die
              // der Dienst den Abschnitt findet, gehört nicht in den Kopf.
              ziel.kfrArt === 'abschnitt'
                ? null
                : beschriftung(vorschau.positionsnamen) || mitNamen(vorschau.positionscode),
            ]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      fuss={
        <>
          {!fertig && darfAnlegen && (
            <button
              ref={anlegenknopf}
              type="button"
              className="haupt"
              data-anlegen
              disabled={!bereit || laeuft}
              onClick={() => void anlegen()}
            >
              {t('anlage.anlegen')}
            </button>
          )}
          <button type="button" onClick={aufSchliessen}>
            {t('import.schliessen')}
          </button>
        </>
      }
    >
      <div className="strukturanlage">

        {/*
          **Was nicht geht, steht als Satz da und nicht als leeres Formular**
          (R145, R495). Das Menü bietet den Punkt gar nicht erst an; kommt
          jemand trotzdem hierher — über einen weitergegebenen Verweis, über
          eine inzwischen geänderte Struktur —, liest er den Grund.
        */}
        {hindernis && vorschau && (
          <p className="hinweis">
            {t(hindernis as Sprachschluessel, { position: mitNamen(vorschau.positionscode) })}
          </p>
        )}

        {!hindernis && (
          <>
            <div className="anlagefelder">
              {/*
                **Eine Kontonummer ist eine Nummer** (R503) — der Code einer
                Position ist es nicht: *A.I.5* ist die Fundstelle im Gesetz.
                Deshalb prüft das Feld nur dort, wo es etwas zu prüfen gibt.
              */}
              {istKfr && (
              <label className="feld">
                <span className="name">{t('anlage.code')}</span>
                <input
                  ref={erstesFeld}
                  value={code}
                  disabled={laeuft || fertig !== null}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              )}

              <label className="feld">
                <span className="name">{t('anlage.bezeichnung')}</span>
                <input
                  ref={istKfr ? undefined : erstesFeld}
                  value={name}
                  disabled={laeuft || fertig !== null}
                  onChange={(e) => setName(e.target.value)}
                />

                {/*
                  **Der Satz nennt die Reihe, aus der die Nummer kommt** (R575)
                  — und er steht nur, solange der Vorschlag unangetastet vorn
                  im Feld steht. Wer eigenes getippt hat, liest keine Auskunft
                  mehr über etwas, das nicht mehr dasteht.
                */}
                {!istKfr &&
                  vorschau !== null &&
                  codevorschlag !== null &&
                  name === `${codevorschlag} ` && (
                    <span className="hinweis">
                      {t(istKonto ? 'anlage.nummer.vorschlag' : 'anlage.code.vorschlag', {
                        position: mitNamen(vorschau.positionscode),
                      })}
                    </span>
                  )}
              </label>

              {/*
                **Was eine Summenzeile zählt** (R1358) — leer „Bitte
                auswählen", wie jede Wahl, die noch aussteht (R1355).
              */}
              {istKfr && summenzeile && (
                <label className="feld breit">
                  <span className="name">{t('kfreigenschaften.zaehlt')}</span>
                  <Auswahlfeld
                    wert={zaehlweise}
                    abgeschaltet={laeuft || fertig !== null}
                    leer={t('auswahl.bitte')}
                    beschriftung={t('kfreigenschaften.zaehlt')}
                    eintraege={[
                      { wert: 'darueber', text: t('stammdaten.wert.zaehlweise.darueber') },
                      { wert: 'seit_voriger', text: t('stammdaten.wert.zaehlweise.seit_voriger') },
                    ]}
                    aufWahl={setZaehlweise}
                  />
                </label>
              )}

              {/*
                **Die Periode steht sichtbar da und ist änderbar** (R499): Sie
                ist die kleinste Struktureinheit (R2), und eine still
                angenommene Periode wäre bei einer zeitbezogenen Zuordnung die
                teuerste Annahme im Modell. Geschrieben wird sie MM.JJJJ wie
                jede Periode in der Anwendung (R502).
              */}
              {!istKfr && (
                <Periodenfeld
                  wert={abPeriode}
                  aufWert={setAbPeriode}
                  beschriftung={t('anlage.abperiode')}
                  klasse="feld"
                  gesperrt={laeuft || fertig !== null}
                />
              )}

              {/*
                **Geraten wird nicht** (R497): Sind die Geschwister uneins oder
                gibt es keine, wird die Kontengruppe zur Pflichtangabe — und
                ausgesucht, nicht getippt (R327).
              */}
              {gruppePflicht && vorschau && (
                <label className="feld breit">
                  <span className="name">{t('anlage.gruppe')}</span>
                  <Auswahlfeld
                    wert={gruppe}
                    abgeschaltet={laeuft || fertig !== null}
                    leer="—"
                    beschriftung={t('anlage.gruppe')}
                    eintraege={vorschau.gruppenvorrat.map((g) => ({
                      wert: g.schluessel,
                      text: katalogbezeichnung(g),
                      suchtext: [g.schluessel, ...Object.values(g.namen)].join(' '),
                      stillgelegtAb: g.stillgelegtAb,
                    }))}
                    aufWahl={setGruppe}
                  />
                  <span className="hinweis">
                    {vorschau.uneinig.length > 0
                      ? t('anlage.gruppe.uneins', {
                          position: mitNamen(vorschau.positionscode),
                          gruppen: vorschau.uneinig.join(', '),
                        })
                      : t('anlage.gruppe.ohne', { position: mitNamen(vorschau.positionscode) })}
                  </span>
                </label>
              )}
            </div>

            {/*
              **Die Unterposition macht die Elternzeile zur Summenzeile**
              (R501). Das steht vorher da: Es ist die einzige Änderung, die
              diese Maske an einem *vorhandenen* Objekt vornimmt.
            */}
            {!istKonto && vorschau?.machtZurSumme && (
              <p className="erlaeuterung">
                {istKfr
                  ? // Was die Position zog, bleibt an ihr, und sie zählt die
                    // neue mit (2.1 Abschnitt 3.6, R1709).
                    t('anlage.kfr.machtzursumme', { position: vorschau.positionscode })
                  : t('anlage.machtzursumme', { position: mitNamen(vorschau.positionscode) })}
              </p>
            )}


            {/*
              **Das Geerbte steht nicht da** (R1578, 2026-10-11): Bis dahin
              stand hier die Tafel „Was es von seiner Stelle erbt“ — für
              KFR-Zeilen schon seit R1357 nicht mehr. Was das Neue erbt, zeigt
              das Eigenschaftsblatt mit seiner Herkunft.
            */}
            {vorschauabruf.ausgefallen && <Abrufausfall abruf={vorschauabruf} />}
          </>
        )}

        {meldung && <p className="hinweis">{meldung}</p>}

        {/*
          **Hier stand bis zum 2026-09-16 „der Bericht zeigt es noch nicht"**
          (R498). Der Satz ist gestrichen, weil er nicht mehr stimmte: Die
          Laufvorsorge (R294) holt den Lauf beim nächsten Abruf nach, und
          `aufAngelegt` stößt diesen Abruf jetzt an (R569). Was hier stünde,
          wäre eine Auskunft über einen Zustand, den die Anwendung nicht mehr
          hat — und die teuerste Sorte: Sie schickte den Anwender von der
          Zeile weg, die hinter diesem Fenster gerade entsteht.

          **Die offenen Positionspläne bleiben** (R4): Sie sind keine Frage
          des Rechnens, sondern eine fehlende Zuordnung.
        */}
        {/* Der Hinweis bleibt am Ort: Er ist keine Bestätigung, sondern eine Lücke (R145, R1132). */}
        {fertig && fertig.offenePlaene > 0 && (
          <div className="probe abweichung" role="status">
            <p>{t('anlage.offeneplaene', { anzahl: anzahlwort(fertig.offenePlaene) })}</p>
          </div>
        )}
      </div>
    </Dialogfenster>
  )
}

/** Angelegt — und in wie vielen weiteren Positionsplänen das Konto noch keine Stelle hat (R4). */
type Fertigmeldung = { offenePlaene: number }

/** Die Überschrift nennt, was entsteht — Position, Konto oder Summenzeile (R1705). */
function titelschluessel(
  art: Anlageziel['art'],
  summenzeile?: boolean,
): 'anlage.titel.konto' | 'anlage.titel.position' | 'anlage.titel.kfrzeile' | 'anlage.titel.kfrsummenzeile' {
  switch (art) {
    case 'konto':
      return 'anlage.titel.konto'
    case 'kfrzeile':
      return summenzeile ? 'anlage.titel.kfrsummenzeile' : 'anlage.titel.kfrzeile'
    default:
      return 'anlage.titel.position'
  }
}
